import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Tag, ArrowRight, Info } from "lucide-react";
import Seo, { SITE_URL, breadcrumbLd, faqLd, organizationLd } from "../components/Seo";
import { track } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONCERN_LABEL = {
  low: "Low concern",
  moderate: "Moderate concern",
  "context-dependent": "Depends on context",
  "avoid-if-sensitive": "Avoid if sensitive",
};

const Header = () => (
  <header className="blog-top">
    <Link to="/" className="blog-brand">
      InZoRAH
    </Link>
    <nav aria-label="Site navigation">
      <Link
        to="/ingredients"
        onClick={() => track("click_ingredients_az", { label: "category header" })}
      >
        Ingredients A-Z
      </Link>
      <Link
        to="/ingredient"
        onClick={() => track("click_ingredient_blog", { label: "category header" })}
      >
        Ingredient blog
      </Link>
    </nav>
  </header>
);

const CategoryPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    axios
      .get(`${API}/glossary-categories/${slug}`)
      .then((r) => {
        setData(r.data);
        setStatus("ok");
      })
      .catch(() => setStatus("missing"));
  }, [slug]);

  const jsonLd = useMemo(() => {
    if (!data) return [];
    const c = data.category;
    const list = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: c.h1,
      url: `${SITE_URL}/ingredients/category/${c.slug}`,
      description: c.meta_description,
      inLanguage: "en",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: data.items.length,
        itemListElement: data.items.map((i, n) => ({
          "@type": "ListItem",
          position: n + 1,
          name: i.name,
          url: `${SITE_URL}/ingredients/${i.slug}`,
        })),
      },
    };
    return [
      list,
      organizationLd,
      c.faq && c.faq.length ? faqLd(c.faq) : null,
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "Ingredient glossary", path: "/ingredients" },
        { name: c.label, path: `/ingredients/category/${c.slug}` },
      ]),
    ].filter(Boolean);
  }, [data]);

  if (status === "loading") {
    return (
      <div className="blog-shell">
        <Header />
        <main className="blog-main">
          <p className="blog-state">Loading category…</p>
        </main>
      </div>
    );
  }

  if (status === "missing" || !data) {
    return (
      <div className="blog-shell">
        <Seo
          title="Category not found | InZoRAH glossary"
          description="This ingredient category could not be found."
          path={`/ingredients/category/${slug}`}
          noindex
        />
        <Header />
        <main className="blog-main">
          <h1 className="blog-h1">Category not found</h1>
          <p className="blog-intro">
            <Link to="/ingredients">Browse the A-Z ingredient glossary</Link> instead.
          </p>
        </main>
      </div>
    );
  }

  const c = data.category;

  return (
    <>
      <Seo
        title={`${c.meta_title} | InZoRAH`}
        description={c.meta_description}
        path={`/ingredients/category/${c.slug}`}
        keywords={`what ingredients, ${c.label.toLowerCase()}, ${c.label.toLowerCase()} list, ingredient glossary`}
        jsonLd={jsonLd}
      />
      <div className="blog-shell">
        <Header />
        <main className="blog-main">
          <nav className="blog-crumbs" aria-label="Breadcrumb">
            <Link to="/ingredients">
              <ArrowLeft size={13} aria-hidden="true" /> All ingredients A-Z
            </Link>
          </nav>

          <p className="blog-eyebrow">{c.label}</p>
          <h1 className="blog-h1">{c.h1}</h1>
          {c.intro && <p className="blog-intro">{c.intro}</p>}

          {(c.what_matters || []).length > 0 && (
            <aside className="post-takeaways" aria-label="What matters">
              <h2>What actually matters here</h2>
              <ul>
                {c.what_matters.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </aside>
          )}

          {c.how_to_read && (
            <section className="cat-read">
              <h2>How to spot them on a label</h2>
              <p>{c.how_to_read}</p>
            </section>
          )}

          <h2 className="cat-list-h">
            Every entry in this group ({data.items.length})
          </h2>
          <div className="gl-grid">
            {data.items.map((i) => (
              <article className="gl-card" key={i.slug}>
                <Link to={`/ingredients/${i.slug}`}>
                  <h3>{i.name}</h3>
                  <p>{i.one_liner}</p>
                  <div className="gl-card-foot">
                    <span className="gl-cat">
                      <Tag size={11} aria-hidden="true" /> {i.category}
                    </span>
                    <span className={`gl-pill lvl-${(i.concern_level || "").replace(/\s/g, "-")}`}>
                      {CONCERN_LABEL[i.concern_level] || i.concern_level}
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          {(c.faq || []).length > 0 && (
            <section className="post-faq">
              <h2>Questions people also ask</h2>
              {c.faq.map((f) => (
                <div key={f.question}>
                  <h3>{f.question}</h3>
                  <p>{f.answer}</p>
                </div>
              ))}
            </section>
          )}

          <section className="cat-siblings">
            <h2>Other ingredient groups</h2>
            <div className="cat-chips">
              {(data.siblings || [])
                .filter((s) => s.slug !== c.slug)
                .map((s) => (
                  <Link
                    key={s.slug}
                    to={`/ingredients/category/${s.slug}`}
                    onClick={() => track("click_category", { label: s.label })}
                  >
                    {s.label} <em>{s.count}</em>
                  </Link>
                ))}
            </div>
          </section>

          <p className="post-disclaimer">
            <Info size={12} aria-hidden="true" /> Educational information, not medical
            advice. Always read the physical label if you have an allergy.
          </p>

          <section className="blog-cta">
            <h2>Check a whole label at once</h2>
            <p>
              InZoRAH scans the ingredient list and flags only what matters for you, with
              sources — then finds a cleaner product on the same shelf.
            </p>
            <Link
              to="/ingredient"
              className="blog-cta-btn"
              onClick={() => track("click_ingredient_blog", { label: "category cta" })}
            >
              Read the ingredient blog
            </Link>
          </section>
        </main>
        <footer className="blog-foot">
          KYSolutions LLC · <Link to="/ingredients">Ingredients A-Z</Link> ·{" "}
          <Link to="/ingredient">Ingredient blog</Link> ·{" "}
          <Link to="/contact">Contact</Link> ·{" "}
          <a
            className="inz-support-link"
            href="https://royalelite.support"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-portal-link"
          >
            Support
          </a>{" "}
          · <Link to="/">Home</Link> ·{" "}
          <Link to="/admin/login" rel="nofollow">
            Admin
          </Link>
        </footer>
      </div>
    </>
  );
};

export default CategoryPage;

/** Category chip row reused on the glossary index. */
export const CategoryChips = () => {
  const [cats, setCats] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/glossary-categories`)
      .then((r) => setCats(r.data?.items || []))
      .catch(() => setCats([]));
  }, []);

  if (!cats.length) return null;

  return (
    <section className="cat-siblings" aria-label="Browse by ingredient group">
      <h2>Browse by group</h2>
      <div className="cat-chips">
        {cats.map((s) => (
          <Link
            key={s.slug}
            to={`/ingredients/category/${s.slug}`}
            onClick={() => track("click_category", { label: s.label })}
          >
            {s.label} <em>{s.count}</em>
          </Link>
        ))}
      </div>
      <p className="cat-note">
        Each group has its own page <ArrowRight size={12} aria-hidden="true" /> useful when
        you want to compare every sweetener or every preservative at once.
      </p>
    </section>
  );
};
